import { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, Volume2, Monitor, Moon, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { getNotifPrefs, setNotifPrefs, playChime } from "@/lib/adminNotify";
import {
  enableAdminPush,
  disableAdminPush,
  isAdminPushActive,
} from "@/lib/adminPush";

export default function AdminNotificationsPage() {
  const [prefs, setPrefs] = useState(getNotifPrefs);
  const [pushActive, setPushActive] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushDeviceCount, setPushDeviceCount] = useState(0);

  useEffect(() => {
    isAdminPushActive().then(setPushActive);
    api.get("/admin/push/status").then((r) => setPushDeviceCount(r.data?.subscriber_count || 0)).catch(() => {});
  }, []);

  const update = useCallback((patch) => {
    setPrefs((p) => {
      const next = { ...p, ...patch, events: { ...p.events, ...(patch.events || {}) } };
      setNotifPrefs(next);
      return next;
    });
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushActive) {
        await disableAdminPush();
        setPushActive(false);
        toast.success("Phone notifications turned off");
      } else {
        await enableAdminPush();
        setPushActive(true);
        toast.success("Phone notifications enabled");
      }
      const s = await api.get("/admin/push/status");
      setPushDeviceCount(s.data?.subscriber_count || 0);
    } catch (e) {
      toast.error(e?.message || "Could not toggle phone notifications");
    } finally {
      setPushBusy(false);
    }
  };

  const sendTestPush = async () => {
    try {
      const r = await api.post("/admin/push/test");
      toast.success(`Test push sent to ${r.data.sent_to_devices} device(s)`);
    } catch {
      toast.error("Test push failed");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-6 mt-2 pb-12" data-testid="admin-notifications-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">
          Control how and when you get alerted about new chats, escalations and submissions.
        </p>
      </div>

      {/* Master switch */}
      <Card>
        <Row>
          <Icon><Bell className="text-rose-500" /></Icon>
          <Label
            title="Notifications"
            description="Master switch — flipping this off stops every alert below."
          />
          <Toggle
            checked={prefs.enabled}
            onChange={(v) => update({ enabled: v })}
            testId="notif-master"
          />
        </Row>
      </Card>

      {/* Channels */}
      <Card title="Channels" hidden={!prefs.enabled}>
        <Row>
          <Icon><Monitor className="text-violet-500" /></Icon>
          <Label
            title="Phone push (PWA)"
            description={
              pushActive
                ? `Active on ${pushDeviceCount} device${pushDeviceCount === 1 ? "" : "s"}. Tap "Send test" to verify.`
                : "Get a native banner on your phone or desktop. Requires installing the admin app via Add-to-Home-Screen."
            }
          />
          <button
            type="button"
            onClick={togglePush}
            disabled={pushBusy}
            data-testid="notif-push-toggle"
            className={`h-9 px-3 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
              pushActive
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {pushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : pushActive ? "ON · Disable" : "Enable"}
          </button>
        </Row>
        {pushActive && (
          <Row tight>
            <Icon hidden />
            <button
              type="button"
              onClick={sendTestPush}
              data-testid="notif-push-test"
              className="text-xs font-semibold text-rose-600 hover:underline"
            >
              Send a test push to my devices
            </button>
          </Row>
        )}

        <Row>
          <Icon><Volume2 className="text-sky-500" /></Icon>
          <Label
            title="In-tab sound"
            description="A 2-note chime in the admin dashboard when a new chat arrives."
          />
          <Toggle
            checked={prefs.sound}
            onChange={(v) => update({ sound: v })}
            testId="notif-sound"
          />
        </Row>
        {prefs.sound && (
          <Row tight>
            <Icon hidden />
            <button
              type="button"
              onClick={playChime}
              data-testid="notif-sound-test"
              className="text-xs font-semibold text-sky-600 hover:underline"
            >
              Preview chime
            </button>
          </Row>
        )}

        <Row>
          <Icon><Sparkles className="text-amber-500" /></Icon>
          <Label
            title="Favicon flash + desktop banner"
            description="If your tab is in the background, the favicon turns red and (if you allow) a browser notification pops up."
          />
          <Toggle
            checked={prefs.desktop}
            onChange={(v) => update({ desktop: v })}
            testId="notif-desktop"
          />
        </Row>
      </Card>

      {/* Events */}
      <Card title="What I get alerted about" hidden={!prefs.enabled}>
        <Row>
          <Icon hidden />
          <Label title="New manager-requested chat" description="A customer asked for a human or EVA handed off." />
          <Toggle
            checked={prefs.events.new_chat}
            onChange={(v) => update({ events: { new_chat: v } })}
            testId="notif-event-new-chat"
          />
        </Row>
        <Row>
          <Icon hidden />
          <Label title="New submission received" description="Any new gift-card submission lands in your inbox." />
          <Toggle
            checked={prefs.events.new_submission}
            onChange={(v) => update({ events: { new_submission: v } })}
            testId="notif-event-new-submission"
          />
        </Row>
      </Card>

      {/* Quiet hours */}
      <Card title="Quiet hours" hidden={!prefs.enabled}>
        <Row>
          <Icon><Moon className="text-indigo-500" /></Icon>
          <Label
            title="Do not disturb window"
            description="No sound, no flash, no desktop banner inside this window. Phone push still respects your phone's OS-level Do Not Disturb."
          />
        </Row>
        <Row tight>
          <Icon hidden />
          <div className="flex items-center gap-3 ml-1">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">From</div>
              <input
                type="time"
                value={prefs.quiet_start || ""}
                onChange={(e) => update({ quiet_start: e.target.value })}
                data-testid="notif-quiet-start"
                className="h-9 px-2 rounded-lg border border-slate-200 text-sm"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">To</div>
              <input
                type="time"
                value={prefs.quiet_end || ""}
                onChange={(e) => update({ quiet_end: e.target.value })}
                data-testid="notif-quiet-end"
                className="h-9 px-2 rounded-lg border border-slate-200 text-sm"
              />
            </div>
            {(prefs.quiet_start || prefs.quiet_end) && (
              <button
                type="button"
                onClick={() => update({ quiet_start: "", quiet_end: "" })}
                className="text-xs text-slate-500 hover:text-slate-900 self-end pb-2"
              >
                Clear
              </button>
            )}
          </div>
        </Row>
      </Card>

      {!prefs.enabled && (
        <div className="mt-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-center gap-2">
          <BellOff className="w-4 h-4" /> Notifications are currently OFF. Nothing will ping, beep or pop up until you flip the master switch back on.
        </div>
      )}
    </div>
  );
}

function Card({ title, children, hidden }) {
  if (hidden) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-4 shadow-sm">
      {title && <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">{title}</div>}
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ children, tight }) {
  return (
    <div className={`flex items-center justify-between gap-4 ${tight ? "py-1" : "py-2.5"}`}>
      {children}
    </div>
  );
}

function Icon({ children, hidden }) {
  if (hidden) return <div className="w-9 h-9" />;
  return (
    <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
      {children && Array.isArray(children) === false ? <span className="w-4 h-4 inline-flex">{children}</span> : children}
    </div>
  );
}

function Label({ title, description }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      {description && <div className="text-xs text-slate-500 mt-0.5">{description}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}
